import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authUpdateMe } from '../services/api.auth';

const STORAGE_KEY = 'tradopro-theme';
const ACCENT_STORAGE_KEY = 'tradopro-accent';
const BACKGROUND_STORAGE_KEY = 'tradopro-background-tone';
const VALID_THEMES = ['system', 'light', 'dark', 'midnight', 'glass'];
const VALID_ACCENTS = ['blue', 'teal', 'amber', 'rose', 'violet'];
const VALID_BACKGROUNDS = ['obsidian', 'graphite', 'navy', 'forest', 'plum'];

export const ACCENT_OPTIONS = [
  {
    id: 'blue',
    label: 'Signal Blue',
    color: '#4f8cff',
    soft: 'rgba(79, 140, 255, 0.22)',
    border: 'rgba(125, 173, 255, 0.58)',
    text: '#eef5ff',
  },
  {
    id: 'teal',
    label: 'Mint',
    color: '#20d6c7',
    soft: 'rgba(32, 214, 199, 0.2)',
    border: 'rgba(94, 234, 212, 0.52)',
    text: '#e8fffb',
  },
  {
    id: 'amber',
    label: 'Gold',
    color: '#f8b84e',
    soft: 'rgba(248, 184, 78, 0.2)',
    border: 'rgba(251, 191, 36, 0.5)',
    text: '#fff7df',
  },
  {
    id: 'rose',
    label: 'Coral',
    color: '#ff5573',
    soft: 'rgba(255, 85, 115, 0.2)',
    border: 'rgba(251, 113, 133, 0.52)',
    text: '#fff0f3',
  },
  {
    id: 'violet',
    label: 'Iris',
    color: '#9b7cff',
    soft: 'rgba(155, 124, 255, 0.2)',
    border: 'rgba(196, 181, 253, 0.52)',
    text: '#f4f0ff',
  },
];

export const BACKGROUND_OPTIONS = [
  {
    id: 'obsidian',
    label: 'Carbon',
    swatch: 'linear-gradient(145deg, #0c0f14, #171b22)',
    solid: '#0c0f14',
    shell: '#0c0f14',
    gradient: 'radial-gradient(circle at 18% 8%, rgba(125,173,255,0.09) 0%, rgba(125,173,255,0) 30%), linear-gradient(145deg, #0c0f14, #171b22)',
    shellOverlay: 'rgba(13, 16, 22, 0.96)',
    sheet: '#12161d',
    track: '#151922',
    thumb: '#3a4352',
    thumbHover: '#566174',
  },
  {
    id: 'graphite',
    label: 'Slate',
    swatch: 'linear-gradient(145deg, #111827, #253044)',
    solid: '#111827',
    shell: '#111827',
    gradient: 'radial-gradient(circle at 20% 10%, rgba(148,163,184,0.16) 0%, rgba(148,163,184,0) 32%), linear-gradient(145deg, #111827, #253044)',
    shellOverlay: 'rgba(17, 24, 39, 0.96)',
    sheet: '#172033',
    track: '#1d273a',
    thumb: '#475569',
    thumbHover: '#64748b',
  },
  {
    id: 'navy',
    label: 'Harbor',
    swatch: 'linear-gradient(145deg, #0a1628, #12395b)',
    solid: '#0a1628',
    shell: '#0a1628',
    gradient: 'radial-gradient(circle at 22% 10%, rgba(56,189,248,0.2) 0%, rgba(56,189,248,0) 30%), linear-gradient(145deg, #0a1628, #12395b)',
    shellOverlay: 'rgba(10, 22, 40, 0.96)',
    sheet: '#0f2138',
    track: '#122945',
    thumb: '#3b668d',
    thumbHover: '#5784ad',
  },
  {
    id: 'forest',
    label: 'Evergreen',
    swatch: 'linear-gradient(145deg, #0b1713, #183a2e)',
    solid: '#0b1713',
    shell: '#0b1713',
    gradient: 'radial-gradient(circle at 20% 12%, rgba(45,212,191,0.16) 0%, rgba(45,212,191,0) 30%), linear-gradient(145deg, #0b1713, #183a2e)',
    shellOverlay: 'rgba(11, 23, 19, 0.96)',
    sheet: '#10241e',
    track: '#143027',
    thumb: '#3d7565',
    thumbHover: '#5a9b88',
  },
  {
    id: 'plum',
    label: 'Orchid',
    swatch: 'linear-gradient(145deg, #171021, #33224f)',
    solid: '#171021',
    shell: '#171021',
    gradient: 'radial-gradient(circle at 22% 10%, rgba(196,181,253,0.18) 0%, rgba(196,181,253,0) 32%), linear-gradient(145deg, #171021, #33224f)',
    shellOverlay: 'rgba(23, 16, 33, 0.96)',
    sheet: '#211730',
    track: '#291d3d',
    thumb: '#68538b',
    thumbHover: '#8c74b4',
  },
];

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

function resolveSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveAccentConfig(accent) {
  return ACCENT_OPTIONS.find((option) => option.id === accent) || ACCENT_OPTIONS[0];
}

function resolveBackgroundConfig(backgroundTone) {
  return BACKGROUND_OPTIONS.find((option) => option.id === backgroundTone) || BACKGROUND_OPTIONS[0];
}

function applyToDOM(theme) {
  const resolved = theme === 'system' ? resolveSystemTheme() : theme;
  document.documentElement.setAttribute('data-theme', resolved);
}

function applyAccentToDOM(accent) {
  const resolvedAccent = resolveAccentConfig(accent);
  document.documentElement.style.setProperty('--ui-accent', resolvedAccent.color);
  document.documentElement.style.setProperty('--ui-accent-soft', resolvedAccent.soft);
  document.documentElement.style.setProperty('--ui-accent-border', resolvedAccent.border);
  document.documentElement.style.setProperty('--ui-accent-text', resolvedAccent.text);
}

function applyBackgroundToDOM(backgroundTone) {
  const resolvedBackground = resolveBackgroundConfig(backgroundTone);
  document.documentElement.style.setProperty('--app-background-solid', resolvedBackground.solid);
  document.documentElement.style.setProperty('--app-shell-bg', resolvedBackground.shell);
  document.documentElement.style.setProperty('--app-shell-gradient', resolvedBackground.gradient);
  document.documentElement.style.setProperty('--app-shell-overlay', resolvedBackground.shellOverlay);
  document.documentElement.style.setProperty('--app-shell-sheet', resolvedBackground.sheet);
  document.documentElement.style.setProperty('--app-scrollbar-track', resolvedBackground.track);
  document.documentElement.style.setProperty('--app-scrollbar-thumb', resolvedBackground.thumb);
  document.documentElement.style.setProperty('--app-scrollbar-thumb-hover', resolvedBackground.thumbHover);
  document.body.style.backgroundColor = resolvedBackground.solid;
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return VALID_THEMES.includes(stored) ? stored : 'dark';
  });
  const [accentColor, setAccentColorState] = useState(() => {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY);
    return VALID_ACCENTS.includes(stored) ? stored : 'blue';
  });
  const [backgroundTone, setBackgroundToneState] = useState(() => {
    const stored = localStorage.getItem(BACKGROUND_STORAGE_KEY);
    return VALID_BACKGROUNDS.includes(stored) ? stored : 'obsidian';
  });

  // Apply on mount and whenever theme changes
  useEffect(() => {
    applyToDOM(theme);
  }, [theme]);

  useEffect(() => {
    applyAccentToDOM(accentColor);
  }, [accentColor]);

  useEffect(() => {
    applyBackgroundToDOM(backgroundTone);
  }, [backgroundTone]);

  // Listen for OS color scheme changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyToDOM('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Called by AuthContext after login/validate — syncs to backend value
  const applyUserTheme = useCallback((themeMode) => {
    if (!themeMode || !VALID_THEMES.includes(themeMode)) return;
    localStorage.setItem(STORAGE_KEY, themeMode);
    setThemeState(themeMode);
  }, []);

  const applyUserAppearance = useCallback((themeMode, preferences = {}) => {
    if (themeMode && VALID_THEMES.includes(themeMode)) {
      localStorage.setItem(STORAGE_KEY, themeMode);
      setThemeState(themeMode);
    }

    const nextAccent = preferences?.accent_color;
    if (nextAccent && VALID_ACCENTS.includes(nextAccent)) {
      localStorage.setItem(ACCENT_STORAGE_KEY, nextAccent);
      setAccentColorState(nextAccent);
    }

    const nextBackgroundTone = preferences?.background_tone;
    if (nextBackgroundTone && VALID_BACKGROUNDS.includes(nextBackgroundTone)) {
      localStorage.setItem(BACKGROUND_STORAGE_KEY, nextBackgroundTone);
      setBackgroundToneState(nextBackgroundTone);
    }
  }, []);

  // Called from Settings — instant preview + backend persist
  const setTheme = useCallback(async (newTheme, token) => {
    if (!VALID_THEMES.includes(newTheme)) return;
    localStorage.setItem(STORAGE_KEY, newTheme);
    setThemeState(newTheme);

    if (token) {
      try {
        await authUpdateMe({ theme_mode: newTheme });
      } catch (e) {
        // Non-fatal — theme is already applied locally
        console.warn('Failed to persist theme to backend:', e);
      }
    }
  }, []);

  const setAccentColor = useCallback((nextAccent) => {
    if (!VALID_ACCENTS.includes(nextAccent)) return;
    localStorage.setItem(ACCENT_STORAGE_KEY, nextAccent);
    setAccentColorState(nextAccent);
  }, []);

  const setBackgroundTone = useCallback(async (nextBackgroundTone, token) => {
    if (!VALID_BACKGROUNDS.includes(nextBackgroundTone)) return;
    localStorage.setItem(BACKGROUND_STORAGE_KEY, nextBackgroundTone);
    setBackgroundToneState(nextBackgroundTone);

    if (token) {
      try {
        await authUpdateMe({ preferences: { background_tone: nextBackgroundTone } });
      } catch (e) {
        console.warn('Failed to persist background tone to backend:', e);
      }
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        applyUserTheme,
        applyUserAppearance,
        accentColor,
        setAccentColor,
        backgroundTone,
        setBackgroundTone,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
